// Tiny auth store backed by localStorage. The server (/auth/*) proxies Stremio and only
// hands back what we need, so the profile we persist is just { authKey, id, email }.
import { useSyncExternalStore } from 'react';

const KEY = 'metahub_profile';

export type Profile = { authKey: string; id: string; email: string };
type Consent = { from: string; tos: boolean; privacy: boolean; marketing: boolean };

function read(): Profile | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Profile) : null;
  } catch {
    return null;
  }
}

let profile: Profile | null = read();
const subs = new Set<() => void>();

function persist(next: Profile | null) {
  profile = next;
  if (next) localStorage.setItem(KEY, JSON.stringify(next));
  else localStorage.removeItem(KEY);
  subs.forEach((cb) => cb());
}

async function post(path: string, body: unknown) {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || data.error) throw new Error((data && data.error) || 'Request failed.');
  return data as { authKey: string; user: { id: string; email: string } };
}

export async function login(email: string, password: string) {
  const { authKey, user } = await post('/auth/login', { email, password });
  persist({ authKey, id: user.id, email: user.email });
}

export async function register(email: string, password: string, consent: Consent) {
  const { authKey, user } = await post('/auth/register', { email, password, gdpr_consent: consent });
  persist({ authKey, id: user.id, email: user.email });
}

export async function logout() {
  const current = profile;
  persist(null);
  if (current) await post('/auth/logout', { authKey: current.authKey }).catch(() => {});
}

export function useAuth(): Profile | null {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => profile,
    () => null,
  );
}
